/**
 * useFundamentals — shared hook for company fundamentals.
 *
 * Fetches the lightweight /api/fundamentals summary (sector, industry, market_cap)
 * once per session and caches at module level. All components share the same data.
 *
 * For full detail (float_shares, description) use /api/fundamentals/<ticker> directly.
 */
import { useEffect, useState } from "react";
import requests from "../requests";

export interface FundamentalsSummary {
  sector: string | null;
  industry: string | null;
  market_cap: number | null;
}

export interface FundamentalsDetail extends FundamentalsSummary {
  ticker: string;
  float_shares: number | null;
  description: string | null;
}

// Module-level cache
let _cache: Record<string, FundamentalsSummary> | null = null;
let _inflight: Promise<void> | null = null;

// Module-level cache for full per-ticker detail (sector/industry/market_cap/float_shares/description)
const _detailCache: Record<string, FundamentalsDetail | null> = {};
const _detailInflight: Record<string, Promise<void>> = {};

/** Format a market cap number into a human-readable string: $1.23T, $456.7B, $12.3M */
export function formatMarketCap(cap: number | null | undefined): string {
  if (!cap) return "";
  if (cap >= 1e12) return `$${(cap / 1e12).toFixed(2)}T`;
  if (cap >= 1e9)  return `$${(cap / 1e9).toFixed(1)}B`;
  if (cap >= 1e6)  return `$${(cap / 1e6).toFixed(0)}M`;
  return `$${cap.toLocaleString()}`;
}

/**
 * Returns a lookup function `getFundamentals(ticker) → FundamentalsSummary | null`.
 * Returns null for unknown tickers or while loading.
 */
export function useFundamentals(): (ticker: string) => FundamentalsSummary | null {
  const [cache, setCache] = useState<Record<string, FundamentalsSummary>>(_cache ?? {});

  useEffect(() => {
    if (_cache) { setCache(_cache); return; }

    if (!_inflight) {
      _inflight = requests.get("/api/fundamentals")
        .then(r => {
          _cache = (r.data.fundamentals ?? {}) as Record<string, FundamentalsSummary>;
        })
        .catch(() => { _cache = {}; })
        .finally(() => { setCache(_cache!); });
    } else {
      void _inflight.finally(() => { setCache(_cache!); });
    }
  }, []);

  return (ticker: string) => cache[ticker] ?? null;
}

/**
 * useFundamentalsDetail — fetches the full per-ticker record from
 * /api/fundamentals/<ticker> (sector, industry, market_cap, float_shares,
 * description) on demand. Pass `null`/`undefined` to skip fetching (e.g. while
 * a modal is closed). Results are cached at module level per ticker so
 * re-opening the same ticker doesn't refetch.
 *
 * Returns `{ detail, loading, error }`:
 *   - `detail` is `null` while loading or if unavailable (404/error)
 *   - `loading` is true only on the initial fetch for a ticker
 *   - `error` is true if the fetch failed or the ticker has no fundamentals
 */
export function useFundamentalsDetail(ticker: string | null | undefined): {
  detail: FundamentalsDetail | null;
  loading: boolean;
  error: boolean;
} {
  const key = ticker ? ticker.toUpperCase() : null;
  const [, forceRender] = useState(0);

  useEffect(() => {
    if (!key || key in _detailCache) return;

    if (!(key in _detailInflight)) {
      _detailInflight[key] = requests.get(`/api/fundamentals/${key}`)
        .then(r => { _detailCache[key] = r.data as FundamentalsDetail; })
        .catch(() => { _detailCache[key] = null; })
        .finally(() => {
          delete _detailInflight[key];
          forceRender(n => n + 1);
        });
    } else {
      void _detailInflight[key].finally(() => forceRender(n => n + 1));
    }
  }, [key]);

  if (!key) return { detail: null, loading: false, error: false };

  // Record index type hides that a not-yet-fetched key is undefined at runtime — widen
  // so the `=== undefined` "still loading" check is meaningful (not "always falsy").
  const cached = _detailCache[key] as FundamentalsDetail | null | undefined;
  if (cached === undefined) return { detail: null, loading: true, error: false };
  return { detail: cached, loading: false, error: cached === null };
}
